import { apiRequest, setToken, clearToken, setStoredUser, clearStoredUser } from "./api";

// Account is created unverified - this never returns a token. It returns
// { challengeId, maskedEmail } for the "enter your code" screen (see verifyEmailOtp)
// instead. The user signs in for real, separately, once the email is verified.
// turnstileToken comes from the Cloudflare widget on the create-account form. The
// backend verifies it against /siteverify before writing anything or sending the OTP,
// so a bot POSTing this endpoint directly cannot get an email sent. Passed through
// even when empty: the server owns the "missing token" message, so the two never
// disagree about the wording.
// referralCode is a friend's share-link code (utils/referral.js), or null. An unknown code
// comes back as a 422 - shown on the form, never silently dropped.
export async function signup({ email, password, isTaxPreparer, termsAccepted, turnstileToken, referralCode }) {
  return apiRequest("/auth/signup", {
    method: "POST",
    auth: false,
    body: { email, password, taxPreparer: isTaxPreparer, termsAccepted, turnstileToken, referralCode: referralCode || null },
  });
}

// Returns either { user, token } (email already verified - signed in) or
// { challengeId, maskedEmail } (email still needs verifying - same OTP screen
// signup() uses). The caller tells them apart by checking for "token".
export async function login({ email, password }) {
  const data = await apiRequest("/auth/login", {
    method: "POST",
    auth: false,
    body: { email, password },
  });
  if (data.token) {
    setToken(data.token);
    // expiresAt (epoch ms) is a sibling of `user` in the response, not part of
    // it - merge it onto the stored user so getMsUntilExpiry() can read it.
    setStoredUser({ ...data.user, expiresAt: data.expiresAt });
  }
  return data;
}

// Consumes the 6-digit code emailed for a signup/login challenge and marks the
// email verified. Never returns a token - sign in afterward with login().
export async function verifyEmailOtp({ challengeId, code }) {
  return apiRequest("/auth/verify-email", {
    method: "POST",
    auth: false,
    body: { challengeId, code },
  });
}

// Re-sends a fresh code for an in-progress email-verification challenge (e.g. "Didn't get it? Resend").
export async function resendEmailOtp({ challengeId }) {
  return apiRequest("/auth/verify-email/resend", {
    method: "POST",
    auth: false,
    body: { challengeId },
  });
}

// Always returns the same generic success whether or not the email is registered
// (anti-enumeration - see the backend's AuthController).
export async function forgotPassword(email) {
  return apiRequest("/auth/forgot-password", {
    method: "POST",
    auth: false,
    body: { email },
  });
}

export async function resetPassword({ token, newPassword, confirmPassword }) {
  return apiRequest("/auth/reset-password", {
    method: "POST",
    auth: false,
    body: { token, newPassword, confirmPassword },
  });
}

// POST /auth/logout is a best-effort call — the backend's JWT is stateless
// (nothing to invalidate server-side, see AuthController), so a failed/timed-out
// request must never block clearing the local session.
export async function logout() {
  try {
    await apiRequest("/auth/logout", { method: "POST" });
  } catch {
    // ignore — still clear the local session below
  } finally {
    clearToken();
    clearStoredUser();
  }
}
